import React, { useState } from "react";
import "react-modern-calendar-datepicker/lib/DatePicker.css";
import DatePicker, { utils } from "react-modern-calendar-datepicker";
import {
  ymdToDate,
  dateToYmd,
  formatDate,
  calendarLocale,
} from "../Utilities/Utilities";
import { Calendar } from "react-feather";
import classNames from "classnames";
import { addHours } from "date-fns";

/*
  Example for a date day picker, with a min and max with all the buttons:

  <DateSelectDay
    label="Select a date"
    containerClass="basis-1/3"
    min={new Date(2022, 0, 1)}
    max={new Date(2023, 11, 31)}
    value={new Date(2022, 0, 1)}
    buttons={['clear', 'today']}
    onChange={(value) => { console.log('New value from date picker', value); }}
  />

*/
export function DateSelectDay({
  label = null,
  containerClass = "",
  className = "",
  value = null,
  min = null,
  max = null,
  buttons = [], // buttons to enable to select different spans, options are: 'clear', 'today'
  onChange = () => {},
  ...props
}) {
  const [selectedDay, setSelectedDay] = useState(
    value instanceof Date
      ? dateToYmd(value)
      : value
        ? dateToYmd(new Date(value))
        : dateToYmd(new Date()),
  );
  const [selectedDate, setSelectedDate] = useState(
    value instanceof Date ? value : value ? new Date(value) : null,
  );
  const [minimumDate, setMinimumDate] = useState(
    min instanceof Date ? dateToYmd(min) : null,
  );
  const [maximumDate, setMaximumDate] = useState(
    min instanceof Date ? dateToYmd(max) : null,
  );

  const whenChanged = (value) => {
    const date = value ? addHours(ymdToDate(value), 2) : null;
    setSelectedDay(value);
    setSelectedDate(date);
    onChange(date);
  };

  const formatInputValue = () => {
    if (!selectedDate) return "Pick a date";
    return formatDate(selectedDate.toISOString());
  };

  const renderCustomInput = ({ ref }) => (
    <div
      className={classNames(
        className,
        "relative z-0 flex min-h-[46px] w-full cursor-pointer border border-borderColor bg-white text-gray-700",
      )}
    >
      <div
        className={classNames(
          "pointer-events-none absolute bottom-0 left-4 right-4 top-0 flex max-w-full cursor-pointer items-center justify-start",
        )}
      >
        <Calendar className="mr-2" />
        <span className="mt-1">{formatInputValue()}</span>
      </div>
      <input
        readOnly
        ref={ref}
        className="h-full  min-h-[46px] w-full cursor-pointer appearance-none border-none bg-transparent py-2 pl-10 pr-3 text-gray-900  focus:border-cyan-600 focus:ring focus:ring-cyan-600 focus:ring-opacity-50"
        {...props}
      />
    </div>
  );

  const renderCustomFooter = () => (
    <div className="-mt-4 flex flex-row flex-wrap items-center justify-center">
      {buttons.includes("clear") && (
        <div className="relative mb-3 border-4 border-transparent p-4">
          <button
            className="relative flex h-10 w-16 items-center justify-center rounded-md border border-gray-200 text-base shadow-sm hover:bg-gray-100"
            type="button"
            onClick={() => {
              setSelectedDay(null);
              whenChanged(null);
            }}
          >
            Clear
          </button>
        </div>
      )}
      {buttons.includes("today") && (
        <div className="relative mb-3 border-4 border-transparent p-4">
          <button
            className="relative flex h-10 w-16 items-center justify-center rounded-md border border-gray-200 text-base shadow-sm hover:bg-gray-100"
            type="button"
            onClick={() => {
              setSelectedDay(utils().getToday());
              whenChanged(utils().getToday());
            }}
          >
            Today
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className={`flex flex-col ${containerClass}`}>
      {label && (
        <label>
          <div className="flex flex-row justify-between">
            <span className={`mb-1 block`}>{label}</span>
          </div>
        </label>
      )}
      <DatePicker
        className="w-full"
        value={selectedDay}
        onChange={whenChanged}
        locale={calendarLocale}
        inputPlaceholder="Select a date"
        formatInputText={formatInputValue}
        inputClassName="my-custom-input"
        minimumDate={minimumDate}
        maximumDate={maximumDate}
        renderInput={renderCustomInput}
        colorPrimary="#1F4444"
        colorPrimaryLight="#AACCCC"
        renderFooter={renderCustomFooter}
        calendarPopperPosition="bottom"
        shouldHighlightWeekends
      />
    </div>
  );
}
